import { Button } from "src/components/buttons/shadcn-button";
import { useEffect, useState } from "react";
import HRTitleHeader from "src/components/ui/HRTitleHeader";
import { AttendanceTable } from "./AttendanceTable";
import AddAttendanceForm from "./AddAttendanceForm";
import {
  AttendanceFilters,
  useTodayAttendance,
} from "./hooks/useFetchTodayAttendance";
import {
  SetURLSearchParams,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import { toast } from "react-toastify";
import { AddHolidayDialog } from "src/components/ui/AddHolidayDialog";
import { useDebounce } from "use-debounce";
import { CustomerListPagination } from "../../ManageCustomers/components/customer-pagination";
import { Input } from "src/components/input/input";

const setSearchParamsFilter = (
  updates: Record<string, string>,
  setSearchParams: SetURLSearchParams,
  resetPage = true,
) => {
  setSearchParams((prev) => {
    const next = Object.fromEntries(prev);

    for (const [key, value] of Object.entries(updates)) {
      if (value.trim() === "") {
        delete next[key];
      } else {
        next[key] = value;
      }
    }

    if (resetPage) next.page = "1";

    return next;
  });
};

const STATUS_FILTERS = {
  ABSENT: "ABSENT",
  FULLDAY: "FULLDAY",
  HALFDAY: "HALFDAY",
} as const;

const STATUS_CARDS = [
  {
    key: "ABSENT" as const,
    label: "Absent Today",
    value: (d) => d?.absent_today,
    classes: "bg-red-50 text-red-500",
    activeClasses: "ring-2 ring-red-400",
  },
  {
    key: "FULLDAY" as const,
    label: "Present Today",
    value: (d) => d?.present_today,
    classes: "bg-green-50 text-green-500",
    activeClasses: "ring-2 ring-green-400",
  },
  {
    key: "HALFDAY" as const,
    label: "Half Day Today",
    value: (d) => d?.half_day_today,
    classes: "bg-amber-50 text-amber-500",
    activeClasses: "ring-2 ring-amber-400",
  },
];


const Attendance = () => {
  const navigate = useNavigate();
  const [showForm, setShowForm] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const [failSafePageNumber, setFailSafePageNumber] = useState<number>(1);

  const name = searchParams.get("name") || "";
  const status = searchParams.get("status") || "";
  const page = parseInt(searchParams.get("page") || "1", 10);
  const pagesize = 25;

  const [debouncedName] = useDebounce(name, 500);

  const handleStatusFilterClick = (statusValue: string) => {
    const isActive = status === statusValue;

    setSearchParamsFilter(
      { status: isActive ? "" : statusValue },
      setSearchParams,
    );
  };

  const filters: AttendanceFilters = {
    name: debouncedName,
    status,
    page,
  };

  const { data, isLoading, isFetching } = useTodayAttendance(filters);

  const today = data?.today_attendance;

  const hasNextPage = today?.length === pagesize;
  const hasPrevPage = page > 1;

  useEffect(() => {
    if (!today) return;
    if (today?.length > 0) {
      setFailSafePageNumber(page);
    } else if (failSafePageNumber !== page) {
      toast.info("No more Attendance!");
      setSearchParams({
        ...Object.fromEntries(searchParams),
        page: failSafePageNumber.toString(),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const handleNextPage = () => {
    setFailSafePageNumber(page);
    setSearchParams({
      ...Object.fromEntries(searchParams),
      page: (page + 1).toString(),
    });
  };

  const handlePrevPage = () => {
    if (page === 1) {
      toast.info("This is the first page");
      return;
    }
    setSearchParams({
      ...Object.fromEntries(searchParams),
      page: (page - 1).toString(),
    });
  };

  const handleJumpToPage = (page: number) => {
    setSearchParams({
      ...Object.fromEntries(searchParams),
      page: page.toString(),
    });
  };

  return (
    <div className="pt-6">
      <HRTitleHeader
        title="Attendance Management"
        info="View Todays Attendance of the Employees"
      />

      <div className="mt-6">
        {/* Top Action Button */}
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold">Today's Attendance</h2>
          <div className="flex gap-6 justify-end">
            <Input
              type="text"
              value={name}
              placeholder="Search Employee Name..."
              onChange={(e) =>
                setSearchParamsFilter(
                  { name: e.target.value.toLowerCase() },
                  setSearchParams,
                )
              }
              className="border w-60"
            />

            <Button onClick={() => navigate("/hr/tasks-today")}>
              Submitted Tasks
            </Button>

            <Button onClick={() => navigate("/hr/attendance-history")}>
              Attendance History
            </Button>

            <AddHolidayDialog>
              <Button>Add Holiday</Button>
            </AddHolidayDialog>

            <Button onClick={() => setShowForm(!showForm)}>
              {showForm ? "Close" : "Add Attendance"}
            </Button>
          </div>
        </div>

        <div className="flex gap-6 relative overflow-x-hidden">
          {/* Attendance Table */}

          {isLoading ? (
            <div className="text-center text-gray-500 mt-10 flex items-center justify-center gap-2 w-full">
              <h2>Loading today's attendance</h2>
              <span className="loading loading-dots loading-md"></span>
            </div>
          ) : (
            <div
              className={`transition-all duration-300 ease-in-out ${
                showForm ? "w-[65%]" : "w-full"
              }`}
            >
              <div className="w-full grid grid-cols-3 gap-6 mb-4">
                {STATUS_CARDS.map((card) => {
                  const statusValue = STATUS_FILTERS[card.key];
                  const isActive = status === statusValue;

                  return (
                    <button
                      key={card.key}
                      type="button"
                      onClick={() => handleStatusFilterClick(statusValue)}
                      className={`shadow-lg text-center p-4 rounded-xl transition ${card.classes} ${
                        isActive ? card.activeClasses : ""
                      } hover:scale-[1.02] active:scale-[0.98]`}
                    >
                      {card.label}: {card.value(data) ?? "--"}
                    </button>
                  );
                })}
              </div>

              {Array.isArray(today) && today.length > 0 ? (
                <div className="min-h-[40dvh] max-h-[60dvh] overflow-y-auto overflow-x-hidden">
                  <AttendanceTable data={today} />
                  <div className="flex-grow my-3">
                    <CustomerListPagination
                      currentPageNumber={page}
                      onNext={handleNextPage}
                      onPrev={handlePrevPage}
                      onJumpToPage={handleJumpToPage}
                      isFetching={isFetching}
                      hasPrev={hasPrevPage}
                      hasNext={hasNextPage}
                    />
                  </div>
                </div>
              ) : (
                <div className="text-center text-gray-500 mt-10 min-h-[40dvh]">
                  No attendance records found for today.
                </div>
              )}
            </div>
          )}

          <div
            className={`absolute right-0 top-0 h-fit transform transition-all duration-300 ease-in-out
          ${
            showForm
              ? "translate-x-0 opacity-100 w-full md:w-1/3"
              : "translate-x-full opacity-0 w-0"
          }
           ml-3 mt-6 hidden md:block`}
          >
            <AddAttendanceForm />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Attendance;
